import { Component, inject, PLATFORM_ID } from '@angular/core';
import { ExportButtonComponent } from "../../components/buttons/export-button/export-button.component";
import { ImagesService } from '../../services/general/images.service';
import { SearchComponent } from "../../shared/search/search.component";
import { PaginationComponent } from "../../components/pagination/pagination.component";
import { UsersCardComponent } from "../../components/card/manage-user/users-card/users-card.component";
import {
  AddUserOverlayComponent
} from "../../components/overlays/manage-user/add-user-overlay/add-user-overlay.component";
import { Router } from '@angular/router';
import { ExportService } from '../../services/general/export.service';
import { ToastService } from '../../services/general/toast.service';
import { AdminService } from '../../services/admin/admin.service';
import { PermissionService } from '../../services/permission/permission.service';
import { Admin } from '../../interfaces/responses/admin-response.interfaces';
import { AdminQueryParams } from '../../interfaces/requests/admin.interfaces';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ProgressLoaderComponent } from '../../components/loaders/progress-loader/progress-loader.component';

@Component({
  selector: 'app-manage-users',
  imports: [
    ExportButtonComponent,
    PaginationComponent,
    UsersCardComponent,
    AddUserOverlayComponent,
    CommonModule,
    ReactiveFormsModule,
    ProgressLoaderComponent
  ],
  templateUrl: './manage-users.component.html',
  styleUrl: './manage-users.component.scss'
})
export class ManageUsersComponent {
  _images = inject(ImagesService);
  adminService = inject(AdminService);
  permissionService = inject(PermissionService);
  _fb = inject(FormBuilder);
  _router = inject(Router);
  exportService = inject(ExportService);
  _toast = inject(ToastService);
  platformId = inject(PLATFORM_ID);

  isAddUser: boolean = false;
  viewMode: 'table' | 'grid' = 'table';
  isEditUser: boolean = false;
  selectedAdmin: Admin | null = null;
  isLoading: boolean = false;

  admins: Admin[] = [];
  adminsResponse: PaginationInterface<Admin[]> = { data: [], meta: { page: 1, perPage: 10, total: 5, totalPages: 1 } };
  searchQuery!: FormGroup;

  queryParams: AdminQueryParams = {
    page: 1,
    perPage: 10,
    paginate: true,
    keyword: '',
  };

  ngOnInit(): void {
    this.loadAdmins();

    this.searchQuery = this._fb.group({
      keyword: [''],
    });

    if (isPlatformBrowser(this.platformId)) {
      this.searchQuery.get('keyword')?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((query: string) => {
          this.queryParams.keyword = query.trim();
          this.queryParams.page = 1;
          this.loadAdmins();
        });
    }
  }

  loadAdmins(): void {
    this.isLoading = true;
    this.adminService.getAdmins(this.queryParams).subscribe({
      next: (res) => {
        this.adminsResponse = res;
        this.admins = res.data ?? [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching admins:', err);
        this._toast.error('Error loading admins');
        this.isLoading = false;
      }
    });
  }

  onPageChange(page: number): void {
    this.queryParams.page = page;
    this.loadAdmins();
  }

  addUserClicked() {
    this.selectedAdmin = null;
    this.isEditUser = false;
    this.isAddUser = !this.isAddUser;
  }

  onEditUser(admin: Admin) {
    this.selectedAdmin = admin;
    this.isEditUser = true;
    this.isAddUser = true;
  }

  onCloseUser() {
    this.isAddUser = false;
    this.isEditUser = false;
    this.selectedAdmin = null;
  }

  onUserCreated() {
    this.loadAdmins();
    this.onCloseUser();
  }

  onUserUpdated() {
    this.loadAdmins();
    this.onCloseUser();
  }

  onUserDeleted() {
    this.loadAdmins();
  }

  onStatusToggled() {
    this.loadAdmins();
  }

  /**
   * Handle export based on type
   */
  handleExport(type: 'Excel' | 'PDF' | 'CSV'): void {
    if (this.admins.length === 0) {
      this._toast.warning('No data available to export');
      return;
    }

    const exportData = this.admins.map(admin => ({
      'First Name': admin.firstName || '',
      'Last Name': admin.lastName || '',
      'Other Name': admin.otherName || '',
      'Email': admin.email || '',
      'Mobile Number': admin.mobileNumber || '',
      'Status': admin.isActive ? 'Active' : 'Inactive',
      'Created Date': admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : ''
    }));

    const filename = `admins_${new Date().toISOString().split('T')[0]}`;

    switch (type) {
      case 'Excel':
        this.exportService.exportToExcel(exportData, filename);
        this._toast.success('Admins exported to Excel successfully');
        break;
      case 'PDF':
        this.exportService.exportToPDF(exportData, filename, 'System Admins');
        this._toast.success('Admins exported to PDF successfully');
        break;
      case 'CSV':
        this.exportService.exportToCSV(exportData, filename);
        this._toast.success('Admins exported to CSV successfully');
        break;
    }
  }
}
